/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type {
  ChangeEvent,
  FC,
  JSX,
  KeyboardEvent,
  MouseEvent,
  RefObject,
} from 'react'

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuRenderFn,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import {
  $createTextNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_LOW,
  createCommand,
  PASTE_COMMAND,
  TextNode,
} from 'lexical'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as ReactDOM from 'react-dom'

import { $createEntityReferenceNode } from '../../nodes/entityReferenceNode'
import { mergeRegister } from '@lexical/utils'
import { COMMAND_PRIORITY_EDITOR } from 'lexical'
import { useSearchEntitiesByEntityQuery } from '@crew/apis/crossSearch/crossSearchApis'
import { SearchEntitiesByEntityRequest } from '@crew/apis/crossSearch/models/searchEntitiesByEntity/request'
import { skipToken } from '@reduxjs/toolkit/query'
import { EntityType } from '@crew/enums/domain'
import { CrewEntityIcon } from 'components/elements/crewEntityIcon/crewEntityIcon'
import { useLazyGetLookupProjectRelatedItemQuery } from '@crew/apis/lookup/lookupApis'
import { useTranslation } from '@crew/modules/i18n'

const PUNCTUATION =
  '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;'
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']'

// 参照候補の検出に利用する正規表現の構成要素
const DocumentEntityReferenceRegex = {
  NAME,
  PUNCTUATION,
}

const PUNC = DocumentEntityReferenceRegex.PUNCTUATION

const TRIGGERS = ['!'].join('')

// 参照キーワードとして許容する文字（空白・句読点・トリガー文字を除外）
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]'

// キーワード内で許容する結合パターン（例: 空白、句読点、ピリオド区切り）
// 前後が VALID_CHARS であることを前提にマッチする
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')'

const LENGTH_LIMIT = 75

// 「!keyword」形式の通常参照を検知する正規表現
const AtSignEntityReferenceRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$'
)

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50

// 参照候補ポップアップを開くためのカスタムコマンド（ペイロードはツールバーボタンの DOM 要素）
export const OPEN_REFERENCE_MODAL_COMMAND = createCommand<Element>()

// エイリアス用（結合パターンを含まない）簡易検知の正規表現
const AtSignEntityReferenceRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$'
)

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// EntityType の値を正規表現に安全に埋め込むためにエスケープして結合
const entityReferenceEntityTypePattern = Object.values(EntityType)
  .map((value) => escapeRegex(value))
  .join('|')

// クリップボード貼り付け時の `!entityType:entityRecordId` 形式を検知
const entityReferencePasteRegex = new RegExp(
  `^!(${entityReferenceEntityTypePattern}):(.+)$`
)

const TOOLBAR_REFERENCE_MENU_LISTBOX_ID = 'toolbar-reference-menu-listbox'
const TOOLBAR_REFERENCE_MENU_OPTION_ID_PREFIX = 'toolbar-reference-item-'

// 入力中テキストから参照トリガーに一致する範囲を抽出
function checkForAtSignEntityReference(
  text: string,
  minMatchLength: number
): MenuTextMatch | null {
  let match = AtSignEntityReferenceRegex.exec(text)

  if (match === null) {
    match = AtSignEntityReferenceRegexAliasRegex.exec(text)
  }
  if (match !== null) {
    // 先頭空白はメニュー表示ロジック上は無視されるため、置換開始位置を補正する
    const maybeLeadingWhitespace = match[1]

    const matchingString = match[3]
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      }
    }
  }
  return null
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignEntityReference(text, 0)
}

// typeahead メニューで使用する候補データモデル
class EntityReferenceTypeaheadOption extends MenuOption {
  entityType: EntityType
  entityRecordId: string
  entityName: string
  icon: JSX.Element

  constructor(
    entityType: EntityType,
    entityRecordId: string,
    entityName: string,
    icon: JSX.Element
  ) {
    super(`${entityType}:${entityRecordId}`)
    this.entityType = entityType
    this.entityRecordId = entityRecordId
    this.entityName = entityName
    this.icon = icon
  }
}

// typeahead 候補1行分の表示コンポーネント
function EntityReferenceTypeaheadMenuItem({
  index,
  isSelected,
  onItemMouseDown,
  onItemClick,
  onItemMouseEnter,
  option,
  optionIdPrefix = 'typeahead-item-',
}: {
  index: number
  isSelected: boolean
  onItemMouseDown: (event: MouseEvent<HTMLLIElement>) => void
  onItemClick: (
    option: EntityReferenceTypeaheadOption,
    index: number
  ) => void
  onItemMouseEnter: (index: number) => void
  option: EntityReferenceTypeaheadOption
  optionIdPrefix?: string
}) {
  const handleMouseDown = useCallback(
    (event: MouseEvent<HTMLLIElement>) => {
      onItemMouseDown(event)
    },
    [onItemMouseDown]
  )

  const handleClick = useCallback(() => {
    onItemClick(option, index)
  }, [index, onItemClick, option])

  const handleMouseEnter = useCallback(() => {
    onItemMouseEnter(index)
  }, [index, onItemMouseEnter])

  let className =
    'item crew-bg-default text-crew-gray-4-light dark:text-crew-gray-1-dark'
  if (isSelected) {
    className += ' selected dark:text-crew-gray-4-light'
  }
  return (
    <li
      key={option.key}
      tabIndex={-1}
      className={className}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={optionIdPrefix + index}
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseEnter}
      onClick={handleClick}
    >
      <div className="flex items-center gap-x-2 min-w-0">
        {option.icon}
        <span className="text">{option.entityName}</span>
      </div>
    </li>
  )
}

type EntityReferencePluginProps = {
  entityType: EntityType
  entityRecordId: string
  projectId: string
  disabled: boolean
  editorContainerRef?: RefObject<HTMLDivElement>
}

// `!` 入力による参照候補の検索・選択、および貼り付け変換を提供するプラグイン
export const EntityReferencePlugin: FC<EntityReferencePluginProps> = (
  props
) => {
  const [editor] = useLexicalComposerContext()
  const { t } = useTranslation()

  const [typeaheadQueryString, setTypeaheadQueryString] = useState<
    string | null
  >(null)
  const [toolbarMenuQueryString, setToolbarMenuQueryString] = useState('')
  const [isToolbarReferenceMenuOpen, setIsToolbarReferenceMenuOpen] =
    useState(false)
  const [toolbarButtonReference, setToolbarButtonReference] =
    useState<Element | null>(null)
  const [selectedToolbarOptionIndex, setSelectedToolbarOptionIndex] =
    useState(0)
  const toolbarReferenceMenuRef = useRef<HTMLDivElement | null>(null)
  const toolbarSelectionAtOpenRef = useRef<ReturnType<typeof $getSelection>>(
    null
  )

  // 貼り付け時のエンティティ参照バリデーション用
  const [triggerGetProjectRelatedItem] =
    useLazyGetLookupProjectRelatedItemQuery()

  // クエリ文字列がある場合のみ API パラメータを組み立てる
  // なお、Pluginが読み込まれる際はqueryStringがnullのため、keywordを空文字でAPIリクエストを実行する
  // これは、結果をTypeaheadで表示するにあたり、事前にデータがセットされていないと
  // 画面下方向に表示領域が不足している場合にも画面外に切れて表示されてしまう問題があるため
  const searchEntitiesByEntityParams:
    | SearchEntitiesByEntityRequest
    | undefined = props.disabled
    ? undefined
    : {
        entityRecordId: props.entityRecordId,
        entityType: props.entityType,
        keyword: isToolbarReferenceMenuOpen
          ? toolbarMenuQueryString
          : typeaheadQueryString === null
            ? ''
            : typeaheadQueryString, // クエリ文字列が null の場合は空文字を渡す（API側で空文字は全件検索として扱われる）
        limit: 10,
      }

  const { currentData, isFetching } = useSearchEntitiesByEntityQuery(
    searchEntitiesByEntityParams ?? skipToken
  )

  // `!` をトリガーに typeahead を起動するためのマッチャ
  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('!', {
    minLength: 0,
  })

  // APIレスポンスを typeahead メニュー表示用のオプションに変換
  const options = useMemo(
    () =>
      currentData
        ? currentData.entities.map(
            (result) =>
              new EntityReferenceTypeaheadOption(
                result.entityType,
                result.entityRecordId,
                result.name,
                <CrewEntityIcon
                  entityType={result.entityType}
                  width={20}
                  height={20}
                />
              )
          )
        : [],
    [currentData]
  )

  const onSelectOption = useCallback(
    (
      selectedOption: EntityReferenceTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      // 候補選択時に ReferenceNode へ置換し、後続入力しやすいよう空白を1つ追加
      editor.update(() => {
        const entityReferenceNode = $createEntityReferenceNode(
          selectedOption.entityType,
          selectedOption.entityRecordId,
          selectedOption.entityName
        )
        if (nodeToReplace) {
          nodeToReplace.replace(entityReferenceNode)
          entityReferenceNode.insertAfter($createTextNode(' '))
        } else {
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            selection.insertNodes([entityReferenceNode, $createTextNode(' ')])
          }
        }
        setIsToolbarReferenceMenuOpen(false)
        closeMenu()
      })
    },
    [editor]
  )

  const checkForReferenceMatch = useCallback(
    (text: string) => {
      // 無効時は候補表示を行わない
      if (props.disabled) {
        return null
      }

      // `!` トリガーの成立確認
      const slashMatch = checkForSlashTriggerMatch(text, editor)
      if (slashMatch === null) {
        return null
      }

      // トリガー成立後、実際の置換対象文字列を抽出
      return getPossibleQueryMatch(text)
    },
    [
      checkForSlashTriggerMatch,
      editor,
      props.disabled,
    ]
  )

  // メニュークローズ時に、エディタ親のスクロールを復帰
  const handleEntityReferenceMenuItemClose = useCallback(() => {
    if (props.editorContainerRef?.current) {
      props.editorContainerRef.current.style.overflow = 'auto'
    }
  }, [props.editorContainerRef])

  const openToolbarReferenceMenu = useCallback((buttonElement: Element) => {
    editor.getEditorState().read(() => {
      toolbarSelectionAtOpenRef.current = $getSelection()?.clone() ?? null
    })
    setToolbarButtonReference(buttonElement)
    setToolbarMenuQueryString('')
    setSelectedToolbarOptionIndex(0)
    setIsToolbarReferenceMenuOpen(true)
  }, [editor])

  const closeToolbarReferenceMenu = useCallback(() => {
    setIsToolbarReferenceMenuOpen(false)
    setToolbarButtonReference(null)
    setSelectedToolbarOptionIndex(0)
    toolbarSelectionAtOpenRef.current = null
    if (props.editorContainerRef?.current) {
      props.editorContainerRef.current.style.overflow = 'auto'
    }
  }, [props.editorContainerRef])

  const selectToolbarMenuOption = useCallback(
    (option: EntityReferenceTypeaheadOption) => {
      editor.update(() => {
        if (toolbarSelectionAtOpenRef.current) {
          $setSelection(toolbarSelectionAtOpenRef.current)
        }

        let selection = $getSelection()
        if (!$isRangeSelection(selection)) {
          // selection が復元できない場合のみ末尾にフォールバックする
          $getRoot().selectEnd()
          selection = $getSelection()
        }

        if ($isRangeSelection(selection)) {
          selection.insertNodes([
            $createEntityReferenceNode(
              option.entityType,
              option.entityRecordId,
              option.entityName
            ),
            $createTextNode(' '),
          ])
        }
      })
      toolbarSelectionAtOpenRef.current = null
      closeToolbarReferenceMenu()
      editor.focus()
    },
    [closeToolbarReferenceMenu, editor]
  )

  useEffect(() => {
    if (selectedToolbarOptionIndex < options.length) {
      return
    }

    setSelectedToolbarOptionIndex(Math.max(options.length - 1, 0))
  }, [options.length, selectedToolbarOptionIndex])

  const updateToolbarReferenceMenuPosition = useCallback(() => {
    if (!toolbarButtonReference || !toolbarReferenceMenuRef.current) {
      return
    }

    const anchorRect = toolbarButtonReference.getBoundingClientRect()
    const menuEl = toolbarReferenceMenuRef.current

    // 水平: ボタン中央に対してメニューを中央揃え → 画面端からはみ出さないよう数値でクランプ
    const menuLeft =
      anchorRect.left + anchorRect.width / 2 - menuEl.offsetWidth / 2
    const maxLeft = Math.max(window.innerWidth - menuEl.offsetWidth, 0)
    const clampedLeft = Math.min(Math.max(menuLeft, 0), maxLeft)
    menuEl.style.left = clampedLeft + 'px'

    // 垂直: ボタンが画面上半分ならメニューを下に、下半分なら上に表示（絵文字ピッカーと同一ロジック）
    if (anchorRect.top < window.innerHeight / 2) {
      menuEl.style.top = anchorRect.top + anchorRect.height + 'px'
      menuEl.style.bottom = 'auto'
    } else {
      menuEl.style.bottom = window.innerHeight - anchorRect.top + 'px'
      menuEl.style.top = 'auto'
    }
  }, [toolbarButtonReference])

  // ツールバーメニュー表示中はエディタ親コンテナのスクロールを止める
  useEffect(() => {
    if (!isToolbarReferenceMenuOpen) {
      return
    }

    if (props.editorContainerRef?.current) {
      props.editorContainerRef.current.style.overflow = 'hidden'
    }

    return () => {
      if (props.editorContainerRef?.current) {
        props.editorContainerRef.current.style.overflow = 'auto'
      }
    }
  }, [isToolbarReferenceMenuOpen, props.editorContainerRef])

  // isToolbarReferenceMenuOpen 中だけ useEffect で mousedown を監視し、
  // toolbarReferenceMenuRef 外クリック時は handleClickOutside から closeToolbarReferenceMenu を呼び、
  // cleanup でリスナーを解除してリークを防ぐ。
  useEffect(() => {
    if (!isToolbarReferenceMenuOpen) {
      return
    }

    const handleClickOutside = (event: MouseEvent | globalThis.MouseEvent) => {
      const target = event.target as Node | null
      if (!target) {
        return
      }

      if (toolbarReferenceMenuRef.current?.contains(target)) {
        return
      }

      closeToolbarReferenceMenu()
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [closeToolbarReferenceMenu, isToolbarReferenceMenuOpen])

  // ツールバーボタン基準でポータルメニューの表示位置を決定する（絵文字ピッカーと同じ方式）
  useLayoutEffect(() => {
    if (!isToolbarReferenceMenuOpen) {
      return
    }

    updateToolbarReferenceMenuPosition()
  }, [isToolbarReferenceMenuOpen, options, updateToolbarReferenceMenuPosition])

  // 表示中のスクロール/リサイズに追従して位置を再計算する
  useEffect(() => {
    if (!isToolbarReferenceMenuOpen) {
      return
    }

    const handleViewportChange = () => {
      updateToolbarReferenceMenuPosition()
    }

    window.addEventListener('resize', handleViewportChange)
    window.addEventListener('scroll', handleViewportChange, true)

    return () => {
      window.removeEventListener('resize', handleViewportChange)
      window.removeEventListener('scroll', handleViewportChange, true)
    }
  }, [isToolbarReferenceMenuOpen, updateToolbarReferenceMenuPosition])

  const renderEntityReferenceMenuItem = useCallback<
    MenuRenderFn<EntityReferenceTypeaheadOption>
  >(
    (
      anchorElementRef,
      { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }
    ) => {
      const handleTypeaheadMenuItemMouseDown = (
        event: MouseEvent<HTMLLIElement>
      ) => {
        event.preventDefault()
      }

      const handleTypeaheadMenuItemClick = (
        option: EntityReferenceTypeaheadOption,
        index: number
      ) => {
        setHighlightedIndex(index)
        selectOptionAndCleanUp(option)
      }

      const handleTypeaheadMenuItemMouseEnter = (index: number) => {
        setHighlightedIndex(index)
      }

      // 候補表示中はエディタ親のスクロールを止め、ポップアップ操作を優先
      if (anchorElementRef.current) {
        if (props.editorContainerRef?.current) {
          props.editorContainerRef.current.style.overflow = 'hidden'
        }

        return ReactDOM.createPortal(
          <div className="typeahead-popover entity-reference-menu">
            <ul>
              {options.length > 0 ? (
                options.map((option, i: number) => (
                  <EntityReferenceTypeaheadMenuItem
                    index={i}
                    isSelected={selectedIndex === i}
                    onItemMouseDown={handleTypeaheadMenuItemMouseDown}
                    onItemClick={handleTypeaheadMenuItemClick}
                    onItemMouseEnter={handleTypeaheadMenuItemMouseEnter}
                    key={option.key}
                    option={option}
                  />
                ))
              ) : (
                <li className="item crew-bg-default text-crew-gray-4-light dark:text-crew-gray-1-dark">
                  <div className="flex items-center min-w-0">
                    <span className="text">
                      {isFetching
                        ? t('action.lexical.loadingSuggestions')
                        : t('action.lexical.noMatchingSuggestionsFound')}
                    </span>
                  </div>
                </li>
              )}
            </ul>
          </div>,
          anchorElementRef.current as HTMLElement
        )
      }

      return null
    },
    [isFetching, options, props.editorContainerRef, t]
  )

  const handleToolbarInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setToolbarMenuQueryString(event.target.value)
      setSelectedToolbarOptionIndex(0)
    },
    []
  )

  const handleToolbarMenuArrowDown = useCallback(() => {
    setSelectedToolbarOptionIndex((prev) =>
      Math.min(prev + 1, Math.max(options.length - 1, 0))
    )
  }, [options.length])

  const handleToolbarMenuArrowUp = useCallback(() => {
    setSelectedToolbarOptionIndex((prev) => Math.max(prev - 1, 0))
  }, [])

  const handleToolbarMenuEnter = useCallback(() => {
    if (options.length === 0) {
      return
    }

    selectToolbarMenuOption(options[selectedToolbarOptionIndex])
  }, [options, selectToolbarMenuOption, selectedToolbarOptionIndex])

  const handleToolbarMenuEscape = useCallback(() => {
    closeToolbarReferenceMenu()
    editor.focus()
  }, [closeToolbarReferenceMenu, editor])

  const handleToolbarInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        handleToolbarMenuArrowDown()
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        handleToolbarMenuArrowUp()
        return
      }

      if (event.key === 'Enter') {
        if (options.length === 0) {
          return
        }

        event.preventDefault()
        handleToolbarMenuEnter()
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        handleToolbarMenuEscape()
      }
    },
    [
      handleToolbarMenuArrowDown,
      handleToolbarMenuArrowUp,
      handleToolbarMenuEnter,
      handleToolbarMenuEscape,
      options.length,
    ]
  )

  const handleToolbarMenuItemMouseDown = useCallback(
    (event: MouseEvent<HTMLLIElement>) => {
      event.preventDefault()
    },
    []
  )

  const handleToolbarMenuItemClick = useCallback(
    (option: EntityReferenceTypeaheadOption) => {
      selectToolbarMenuOption(option)
    },
    [selectToolbarMenuOption]
  )

  const handleToolbarMenuItemMouseEnter = useCallback((index: number) => {
    setSelectedToolbarOptionIndex(index)
  }, [])

  // クリップボードからペーストされたエンティティ参照を処理するためのハンドラ。
  // ペースト時点のselectionを保存し、非同期処理後もカーソル位置を復元してノード挿入できるようにする
  const insertEntityReferenceFromClipboard = useCallback(
    (entityType: EntityType, entityRecordId: string, pasteText: string) => {
      // ペースト時点のselectionをシリアライズして保存する
      const savedSelection = $getSelection()?.clone() ?? null

      void (async () => {
        let entityName = pasteText

        try {
          // 貼り付けられたエンティティが同一プロジェクト内に存在するかをバリデーションする
          // projectId をリクエストに含め、バックエンドでプロジェクトスコープの検証を行う
          const response = await triggerGetProjectRelatedItem({
            entityType,
            id: entityRecordId,
            projectId: props.projectId,
          }).unwrap()

          if (!response.relatedItem) {
            // エンティティが見つからない場合、文字列をそのまま挿入
            editor.update(() => {
              if (savedSelection) {
                $setSelection(savedSelection)
              }
              const selection = $getSelection()
              if ($isRangeSelection(selection)) {
                selection.insertText(pasteText)
              }
            })
            return
          }

          // バリデーション成功: エンティティ名をレスポンスから取得
          const relatedItemName = response.relatedItem.name
          if (relatedItemName && relatedItemName.length > 0) {
            entityName = relatedItemName
          }
        } catch {
          // エラーの場合は、文字列がそのままメッセージとして張り付ける
          editor.update(() => {
            if (savedSelection) {
              $setSelection(savedSelection)
            }
            const selection = $getSelection()
            if ($isRangeSelection(selection)) {
              selection.insertText(pasteText)
            }
          })
          return
        }

        editor.update(() => {
          // 保存したselectionを復元
          if (savedSelection) {
            $setSelection(savedSelection)
          }
          const selection = $getSelection()
          if ($isRangeSelection(selection)) {
            selection.insertNodes([
              $createEntityReferenceNode(
                entityType,
                entityRecordId,
                entityName
              ),
            ])
          }
        })
      })()
    },
    [editor, props.projectId, triggerGetProjectRelatedItem]
  )

  useEffect(() => {
    // 参照関連コマンドをまとめて登録する
    const unregister = mergeRegister(
      editor.registerCommand<ClipboardEvent>(
        PASTE_COMMAND,
        (event) => {
          // 無効時は貼り付け変換を行わない
          if (props.disabled) {
            return false
          }

          // `!entityType:entityRecordId` 形式のテキスト貼り付けを ReferenceNode に変換
          const clipboardData = event.clipboardData
          if (clipboardData) {
            const text = clipboardData.getData('text/plain').trim()
            const match = entityReferencePasteRegex.exec(text)
            if (match !== null) {
              const entityType = match[1] as EntityType
              const entityRecordId = match[2]
              event.preventDefault()
              insertEntityReferenceFromClipboard(
                entityType,
                entityRecordId,
                text
              )
              return true
            }
          }
          return false
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        OPEN_REFERENCE_MODAL_COMMAND,
        (buttonElement) => {
          // 無効時は参照モーダルを開かない
          if (props.disabled) {
            return false
          }

          // ツールバーボタン起点で独立ポータルメニューを開く（Typeahead不使用）
          editor.focus()
          openToolbarReferenceMenu(buttonElement)

          return true
        },
        COMMAND_PRIORITY_EDITOR
      )
    )

    return () => {
      // アンマウント時にコマンド登録を解除
      unregister()
    }
  }, [
    editor,
    insertEntityReferenceFromClipboard,
    openToolbarReferenceMenu,
    props.disabled,
    props.projectId,
  ])

  return (
    <>
      <LexicalTypeaheadMenuPlugin<EntityReferenceTypeaheadOption>
        onQueryChange={setTypeaheadQueryString}
        onSelectOption={onSelectOption}
        triggerFn={checkForReferenceMatch}
        options={options}
        onClose={handleEntityReferenceMenuItemClose}
        menuRenderFn={renderEntityReferenceMenuItem}
      />
      {isToolbarReferenceMenuOpen ? (
        ReactDOM.createPortal(
          <div
            ref={toolbarReferenceMenuRef}
            className="typeahead-popover entity-reference-menu fixed z-70"
          >
            <div className="p-2">
              <input
                autoFocus
                className="w-full px-2 py-1 crew-bg-default border border-crew-gray-2-light rounded"
                value={toolbarMenuQueryString}
                onChange={handleToolbarInputChange}
                onKeyDown={handleToolbarInputKeyDown}
                placeholder="関連情報を検索"
                role="combobox"
                aria-expanded={isToolbarReferenceMenuOpen}
                aria-controls={TOOLBAR_REFERENCE_MENU_LISTBOX_ID}
                aria-haspopup="listbox"
                aria-autocomplete="list"
                aria-activedescendant={
                  options.length > 0
                    ? TOOLBAR_REFERENCE_MENU_OPTION_ID_PREFIX +
                      selectedToolbarOptionIndex
                    : undefined
                }
              />
            </div>
            <ul
              id={TOOLBAR_REFERENCE_MENU_LISTBOX_ID}
              role="listbox"
              aria-label="関連情報候補"
            >
              {options.length > 0 ? (
                options.map((option, i: number) => (
                  <EntityReferenceTypeaheadMenuItem
                    index={i}
                    isSelected={selectedToolbarOptionIndex === i}
                    onItemMouseDown={handleToolbarMenuItemMouseDown}
                    onItemClick={handleToolbarMenuItemClick}
                    onItemMouseEnter={handleToolbarMenuItemMouseEnter}
                    key={option.key}
                    option={option}
                    optionIdPrefix={TOOLBAR_REFERENCE_MENU_OPTION_ID_PREFIX}
                  />
                ))
              ) : (
                <li className="item crew-bg-default text-crew-gray-4-light dark:text-crew-gray-1-dark">
                  <div className="flex items-center min-w-0">
                    <span className="text">
                      {isFetching
                        ? t('action.lexical.loadingSuggestions')
                        : t('action.lexical.noMatchingSuggestionsFound')}
                    </span>
                  </div>
                </li>
              )}
            </ul>
          </div>,
          document.body
        )
      ) : null}
    </>
  )
}
